<template>
  <div class="e-dialog">
    <router-link
      v-if="$route.name === 'sentDialog'"
      :to="{ name: 'sent' }"
      class="e-dialog__back"
    >
      <IconChevronLeft />
    </router-link>
    <router-link v-else :to="{ name: 'dialogs' }" class="e-dialog__back">
      <IconChevronLeft />
    </router-link>

    <VuePerfectScrollbar class="e-dialog__inner" ref="scrollContainer">
      <div class="e-dialog__scroll">
        <div
          v-if="currentDialog && currentDialog.counselorsAbsentUntil"
          class="absence-info"
        >
          {{ currentDialog.counselor }} abwesend bis {{ counselorsAbsentUntil }}
        </div>
        <div
          v-if="currentDialog && currentDialog.subject"
          class="e-dialog__subject"
        >
          <h3>{{ currentDialog.subject }}</h3>
          <template v-if="currentDialog">
            <el-button
              v-if="currentDialog.status !== 'finished'"
              class="e-dialog__finish-button"
              :disabled="currentDialog.status === 'finished'"
              @click="onFinishDialogClick"
            >
              <icon-check class="e-dialog__finish-icon" role="presentation" />{{
                $t('messages.dialog.finish')
              }}
            </el-button>
            <el-tooltip v-else :content="$t('messages.dialog.finishedTooltip')">
              <el-button class="e-dialog__finish-button e-dialog__finished">
                <icon-check class="e-dialog__finish-icon" />
              </el-button>
            </el-tooltip>
          </template>
          <div class="e-dialog__count">
            {{ currentDialog.threadCount }}
          </div>
        </div>

        <el-collapse v-model="activeNames">
          <!-- Collapse Item -->
          <el-collapse-item
            v-for="message in messages"
            :key="message.id"
            :name="message.id"
          >
            <template slot="title">
              <DialogItemHead
                :data="message"
                :item-open="activeNames.includes(message.id)"
              />
            </template>
            <article class="e-dialog__item">
              <div
                v-if="messageEditorOpen && hasReply(message.id)"
                class="e-dialog__overlay"
              />
              <DialogItemBody :data="message" :addendum="dialogCounselorMessageAddendum(message)"/>
              <DialogItemFooter
                :data="message"
                :editor-enabled="
                  isLatestCounselorMessage(message.id) && !isFinished
                "
                :send-disabled="isFinished"
                @reply="reply => onReply(message, reply)"
                @delete-item="onDeleteItemClick(message)"
              />
            </article>
            <MessageEditor
              v-if="messageEditorOpen && hasReply(message.id)"
              :edit-message="editMessage"
              :save-status="saveStatus"
              @change="onEdited"
              @send="onSend"
              @cancel="onEditingCancel"
              @delete="onDeleteDraft"
              class="hide-header"
            />
          </el-collapse-item>
        </el-collapse>
      </div>
    </VuePerfectScrollbar>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import { mapGetters } from 'vuex'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { format } from 'date-fns'
import difference from 'lodash/difference'

import IconChevronLeft from 'assets/icons/chevron-left.svg'
import IconCheck from 'assets/icons/arrows-circle-check.svg'

import DialogItemHead from 'molecules/DialogItem/DialogItemHead'
import DialogItemBody from 'molecules/DialogItem/DialogItemBody'
import DialogItemFooter from 'molecules/DialogItem/DialogItemFooter'
import MessageEditor from 'organisms/MessageEditor/MessageEditor'

import DialogModel from 'store/models/Dialog'
import DialogMessage from 'store/models/DialogMessage'

export default {
  name: 'DialogWrapper',
  components: {
    DialogItemHead,
    DialogItemBody,
    DialogItemFooter,
    IconChevronLeft,
    MessageEditor,
    VuePerfectScrollbar,
    IconCheck
  },
  data: () => ({
    activeNames: [],
    saveStatus: 'unsaved',
    messageEditorOpen: false,
    initialEditMessage: {
      id: null,
      subject: '',
      body: ''
    },
    editMessage: {
      id: null,
      subject: '',
      body: ''
    }
  }),
  computed: {
    ...mapGetters([
      'currentDialog',
      'currentDialogId',
      'userInfo',
      'userId',
      'kidMessageSentMessage'
    ]),

    computedMessages() {
      return this.currentDialog?.messages ?? []
    },

    counselorsAbsentUntil() {
      if (this.currentDialog.counselorsAbsentUntil) {
        const absentDate = new Date(this.currentDialog.counselorsAbsentUntil)
        return format(absentDate, 'dd.MM.yyyy')
      }
      return null
    },

    messages() {
      if (this.computedMessages?.[0]?.draft) {
        const messages = this.computedMessages.slice(1)
        return messages
      }
      return this.computedMessages
    },

    counselorMessages() {
      return this.computedMessages.filter(el => el.outgoing === false)
    },

    unreadMessages() {
      return this.messages.filter(el => !el.read && !el.outgoing)
    },

    hasReply() {
      return id => this?.editMessage?.parentId === id
    },

    isLatestCounselorMessage() {
      return id => this.counselorMessages?.[0]?.id === id
    },

    hasUnsavedChanges() {
      return (
        this.initialEditMessage.body !== this.editMessage.body ||
        this.initialEditMessage.subject !== this.editMessage.subject
      )
    },

    onEdited() {
      return debounce(this.autoSaveEditMessage, 3000, {
        leading: false,
        trailing: true
      })
    },
    isFinished() {
      return this.currentDialog && this.currentDialog.status === 'finished'
    }
  },
  watch: {
    computedMessages: {
      handler() {
        this.onMessagesChanged()
        if (!this.$route.params.messageId && this.messages.length > 0) {
          this.openMessage(this.messages[0].id)
        }
      },
      immediate: true
    },
    unreadMessages: {
      handler(newVal, oldVal) {
        if (newVal.length === 0 && oldVal?.length > 0) {
          DialogModel.markAsRead(this.currentDialog.id)
        }
      },
      immediate: true
    },
    activeNames: {
      handler(newVal, oldVal) {
        const openedItems = difference(newVal, oldVal)
        openedItems.forEach(openItem => {
          const message = this.unreadMessages.find(
            unreadMessage => unreadMessage.id === openItem
          )
          if (message !== undefined) {
            DialogMessage.markAsRead(this.userId, message)
          }
        })
      },
      immediate: true
    },
    saveStatus(status) {
      if (['autoSaved', 'sending'].includes(status)) {
        setTimeout(() => {
          this.saveStatus = 'unsaved'
        }, 2000)
      }
    },
    $route: {
      handler() {
        if (this.$route.params.messageId) {
          this.openMessage(this.$route.params.messageId)
        }
      },
      immediate: true
    }
  },

  async beforeDestroy() {
    if (this.hasUnsavedChanges) {
      this.onEdited.cancel()
      await DialogMessage.autoSave(this.userId, this.editMessage)
    }
  },

  methods: {
    setEditMessage(message) {
      const emptyMessage = new DialogMessage()
      this.initialEditMessage = Object.assign({}, emptyMessage, message)
      this.editMessage = Object.assign({}, emptyMessage, message)
    },
    onMessagesChanged() {
      if (this.$route.name !== 'dialog') {
        return
      }
      const editMessage = this.computedMessages.slice(0, 1).shift()
      if (editMessage?.draft) {
        this.setEditMessage(editMessage)
        this.messageEditorOpen = true

        if (editMessage.parentId) {
          this.$nextTick(() => this.openMessage(editMessage.parentId))
        }
      }
    },

    onEditingCancel() {
      this.onEdited.cancel()
      this.setEditMessage()
      this.messageEditorOpen = false
    },

    openMessage(messageId) {
      Object.keys(this.messages).forEach(index => {
        const message = this.messages[index]
        if (message.id === messageId) this.activeNames = [messageId]
      })
    },

    onReply(message, reply) {
      this.messageEditorOpen = false
      let msg = {
        author: this.userInfo.username,
        read: true,
        subject: message.subject,
        parentId: message.id,
        rootId: message.rootId,
        body: reply
      }
      this.saveStatus = 'unsaved'
      this.$nextTick(() => {
        this.setEditMessage(msg)
        this.messageEditorOpen = true
      })
    },

    async autoSaveEditMessage() {
      if (this.saveStatus === 'autoSaving' || this.saveStatus === 'sending') {
        return
      }
      this.saveStatus = 'autoSaving'
      const isNew = this.editMessage.id === null
      await DialogMessage.autoSave(this.userId, this.editMessage)

      if (isNew) {
        DialogModel.setThreadCount(
          this.currentDialogId,
          this.currentDialog.threadCount + 1
        )
      }
      this.saveStatus = 'autoSaved'
    },

    async onSend() {
      this.onEdited.cancel()
      this.saveStatus = 'sending'
      const isNew = this.editMessage.id === null
      try {
        await DialogMessage.send(this.userId, this.editMessage)
      } catch(e) {
        if (e.messages && e.messages.length && e.messages[0] === "messages.dialog.counselorNotInTeam") {
          this.$emit('dialog-change')
          return
        }
        console.error(e)
        return
      }
      this.$successMessage(this.kidMessageSentMessage, { allowHTML: true })
      this.setEditMessage()
      if (isNew) {
        DialogModel.setThreadCount(
          this.currentDialogId,
          this.currentDialog.threadCount + 1
        )
      }
      this.messageEditorOpen = false
      this.$emit('dialog-change')
      setTimeout(() => (this.$refs.scrollContainer.$el.scrollTop = 0), 20)
    },

    async onDeleteDraft() {
      this.onEdited.cancel()
      await DialogMessage.deleteMessage(this.userId, this.editMessage)
      this.setEditMessage()
      this.$successMessage(this.$t('messages.dialog.deleteSuccess'))
      this.$emit('dialog-change')
    },

    onDeleteItemClick(message) {
      this.$confirm(
        this.$t('messages.dialog.deleteItem'),
        this.$t('system.warning'),
        {
          confirmButtonText: this.$t('system.confirm'),
          cancelButtonText: this.$t('system.cancel'),
          type: 'warning'
        }
      ).then(async () => {
        await DialogMessage.deleteMessage(this.userId, message)
        this.$successMessage(this.$t('messages.dialog.deleteSuccess'))
        this.$emit('dialog-change')
      })
    },
    onFinishDialogClick() {
      this.$confirm(
        window.landingTexts.dialogFinishConfirm,
        this.$t('system.attention'),
        {
          confirmButtonText: this.$t('system.confirm'),
          cancelButtonText: this.$t('system.cancel'),
          type: 'warning',
          dangerouslyUseHTMLString: true
        }
      ).then(async () => {
        await DialogModel.finishDialog(this.userId, this.currentDialog.id)
        this.setEditMessage({})
        this.$successMessage(this.$t('messages.dialog.finishSuccess'))
        this.$emit('dialog-change')
      })
    },
    dialogCounselorMessageAddendum(message) {
      if (message.outgoing) {
        return null
      }
      return window.landingTexts.dialogCounselorMessageAddendum
    }
  }
}
</script>

<style lang="scss" scoped>
@import '_assets/dialog';
</style>

<style lang="scss">
// stylelint-disable-next-line
.hide-header .e-dialog__new-subject {
  display: none;
}
</style>
